import { useRef, useState, type DragEvent, type ChangeEvent } from 'react';
import { Box, Typography, Button } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';

interface ImageDropZoneProps {
  label: string;
  onFile: (file: File) => void;
  preview?: string;
  sidebarColor: string;
  hint?: string;
}

function ImageDropZone({ label, onFile, preview, sidebarColor, hint }: ImageDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  };

  return (
    <Box>
      <Box
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        sx={{
          border: `2px dashed ${dragging ? sidebarColor : '#dee2e6'}`,
          borderRadius: 2,
          backgroundColor: dragging ? `${sidebarColor}08` : '#fafafa',
          minHeight: 90,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          p: 1.5,
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}
        onClick={() => inputRef.current?.click()}
      >
        {preview ? (
          <img src={preview} alt={label} style={{ maxHeight: 70, maxWidth: '100%', objectFit: 'contain', borderRadius: 4 }} />
        ) : (
          <>
            <Button
              variant="contained"
              size="small"
              startIcon={<UploadIcon fontSize="small" />}
              onClick={e => { e.stopPropagation(); inputRef.current?.click(); }}
              sx={{
                backgroundColor: sidebarColor,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { backgroundColor: sidebarColor, opacity: 0.9 },
              }}
            >
              Envoyer une image
            </Button>
            <Typography variant="caption" color="text.secondary">
              Drag &amp; drop
            </Typography>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png"
          style={{ display: 'none' }}
          onChange={(e: ChangeEvent<HTMLInputElement>) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
        />
      </Box>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

export default ImageDropZone;
